import React from "react";
import { useNavigate } from "react-router-dom";
import { FiEye, FiShoppingCart, FiEdit2, FiPower } from "react-icons/fi";
import { HiOutlineChevronUp, HiOutlineChevronDown } from "react-icons/hi2";
import Skeleton from "../Loader/Skeleton";

const StatusBadge = ({ status }) => {
  const isActive = status === "Active";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 12px",
        borderRadius: "9999px",
        fontSize: "12px",
        fontWeight: "600",
        background: isActive ? "#E6F9F0" : "#FFF3F3",
        color: isActive ? "#27AE60" : "#C0335A",
        border: `1px solid ${isActive ? "#27AE60" : "#C0335A"}`,
      }}
    >
      {status}
    </span>
  );
};

const TypeBadge = ({ type }) => {
  const isFixed = type === "Fixed";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 12px",
        borderRadius: "9999px",
        fontSize: "12px",
        fontWeight: "600",
        background: isFixed ? "#EFF6FF" : "#F5F3FF",
        color: isFixed ? "#2563EB" : "#7C3AED",
        border: `1px solid ${isFixed ? "#BFDBFE" : "#DDD6FE"}`,
      }}
    >
      {type}
    </span>
  );
};

const SortIcon = ({ col, sortBy, sortOrder }) => {
  const active = sortBy === col;
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", marginLeft: "4px", verticalAlign: "middle" }}>
      <HiOutlineChevronUp
        size={10}
        style={{ color: active && sortOrder === "asc" ? "#C0335A" : "#9CA3AF", marginBottom: "-2px" }}
      />
      <HiOutlineChevronDown
        size={10}
        style={{ color: active && sortOrder === "desc" ? "#C0335A" : "#9CA3AF" }}
      />
    </span>
  );
};

const getEditDisabledReason = (row) => {
  if (row.status !== "Active") return "Plan is already inactive";
  if ((row.subscriberCount ?? 0) > 0) return "Cannot edit — plan has active subscribers";
  return null;
};

const getInactivateDisabledReason = (row) => {
  if (row.status !== "Active") return "Plan is already inactive";
  if ((row.subscriberCount ?? 0) > 0) return "Cannot inactivate — plan has active subscribers";
  return null;
};

const SubscriptionTable = ({
  data,
  loading,
  page,
  limit,
  total,
  sortBy,
  sortOrder,
  onSort,
  onPageChange,
  onLimitChange,
  currency = "AED",
  onPurchase,
  onEdit,
  onInactivate,
  onReactivate,
}) => {
  const navigate = useNavigate();
  const totalPages = Math.ceil(total / limit);

  const thStyle = {
    background: "#F9FAFB",
    fontWeight: "600",
    fontSize: "13px",
    color: "#374151",
    padding: "12px 16px",
    borderBottom: "1px solid #E5E7EB",
    whiteSpace: "nowrap",
    userSelect: "none",
  };

  const thSortable = { ...thStyle, cursor: "pointer" };

  const tdStyle = {
    padding: "12px 16px",
    fontSize: "14px",
    color: "#374151",
    borderBottom: "1px solid #F3F4F6",
    verticalAlign: "middle",
  };

  const handleSort = (col) => {
    onSort(col, sortBy === col && sortOrder === "asc" ? "desc" : "asc");
  };

  const skeletonRows = Array.from({ length: limit });

  // Mobile card view
  const MobileCardView = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {loading
        ? skeletonRows.map((_, i) => (
            <div
              key={i}
              style={{
                background: "#fff",
                borderRadius: "12px",
                padding: "16px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
              }}
            >
              <Skeleton height="16px" marginBottom="8px" />
              <Skeleton width="60%" height="14px" marginBottom="6px" />
              <Skeleton width="40%" height="14px" />
            </div>
          ))
        : data.map((row) => (
            <div
              key={row.id}
              style={{
                background: "#fff",
                borderRadius: "12px",
                padding: "16px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px" }}>
                <div>
                  <div style={{ fontWeight: "600", color: "#111827", fontSize: "15px", marginBottom: "4px" }}>{row.planName}</div>
                  <span
                    style={{
                      border: "1px solid #D1D5DB",
                      background: "#fff",
                      color: "#374151",
                      borderRadius: "6px",
                      padding: "2px 8px",
                      fontSize: "12px",
                    }}
                  >
                    {row.serviceType}
                  </span>
                </div>
                <StatusBadge status={row.status} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", fontSize: "13px", color: "#6B7280", marginBottom: "12px" }}>
                <div>
                  <div style={{ marginBottom: "2px" }}>Type</div>
                  <TypeBadge type={row.type} />
                </div>
                <div>
                  <div style={{ marginBottom: "2px" }}>Pricing</div>
                  <span style={{ color: "#111827", fontWeight: "600" }}>{currency} {Number(row.pricing || 0).toFixed(2)}</span>
                </div>
                <div>
                  <div style={{ marginBottom: "2px" }}>Active Subscribers</div>
                  <span style={{ color: "#374151" }}>{row.subscriberCount ?? 0}</span>
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", flexWrap: "wrap" }}>
                {(() => {
                  const isActive = row.status === "Active";
                  const editReason = getEditDisabledReason(row);
                  const inactivateReason = getInactivateDisabledReason(row);
                  return (
                    <>
                      <button
                        onClick={isActive ? () => onPurchase?.(row) : undefined}
                        disabled={!isActive}
                        title={isActive ? "Purchase plan" : "Plan is inactive"}
                        style={{
                          background: isActive ? "#C0335A" : "#F3F4F6",
                          border: "none",
                          color: isActive ? "#fff" : "#9CA3AF",
                          borderRadius: "8px",
                          padding: "6px 14px",
                          cursor: isActive ? "pointer" : "not-allowed",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          fontSize: "13px",
                          fontWeight: "600",
                          opacity: isActive ? 1 : 0.6,
                        }}
                      >
                        <FiShoppingCart size={15} /> Purchase
                      </button>
                      <button
                        onClick={editReason ? undefined : () => onEdit?.(row)}
                        disabled={!!editReason}
                        title={editReason || "Edit plan"}
                        style={{
                          background: editReason ? "#F3F4F6" : "#EFF6FF",
                          border: `1px solid ${editReason ? "#D1D5DB" : "#BFDBFE"}`,
                          color: editReason ? "#9CA3AF" : "#2563EB",
                          borderRadius: "8px",
                          padding: "6px 14px",
                          cursor: editReason ? "not-allowed" : "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          fontSize: "13px",
                          fontWeight: "600",
                          opacity: editReason ? 0.6 : 1,
                        }}
                      >
                        <FiEdit2 size={14} /> Edit
                      </button>
                      {isActive ? (
                        <button
                          onClick={inactivateReason ? undefined : () => onInactivate?.(row)}
                          disabled={!!inactivateReason}
                          title={inactivateReason || "Inactivate plan"}
                          style={{
                            background: inactivateReason ? "#F3F4F6" : "#FFF7ED",
                            border: `1px solid ${inactivateReason ? "#D1D5DB" : "#FED7AA"}`,
                            color: inactivateReason ? "#9CA3AF" : "#C2410C",
                            borderRadius: "8px",
                            padding: "6px 14px",
                            cursor: inactivateReason ? "not-allowed" : "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "13px",
                            fontWeight: "600",
                            opacity: inactivateReason ? 0.6 : 1,
                          }}
                        >
                          <FiPower size={14} /> Inactivate
                        </button>
                      ) : (
                        <button
                          onClick={() => onReactivate?.(row)}
                          title="Reactivate plan"
                          style={{
                            background: "#F0FDF4",
                            border: "1px solid #86EFAC",
                            color: "#15803D",
                            borderRadius: "8px",
                            padding: "6px 14px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "13px",
                            fontWeight: "600",
                          }}
                        >
                          <FiPower size={14} /> Reactivate
                        </button>
                      )}
                    </>
                  );
                })()}
                <button
                  onClick={() => navigate(`/subscription/${row.id}`)}
                  style={{
                    background: "#FDE8EF",
                    border: "1px solid #C0335A",
                    color: "#C0335A",
                    borderRadius: "8px",
                    padding: "6px 14px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "13px",
                    fontWeight: "600",
                  }}
                >
                  <FiEye size={15} /> View
                </button>
              </div>
            </div>
          ))}
    </div>
  );

  return (
    <div>
      {/* Desktop table */}
      <div className="subscription-table-desktop" style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
          <thead>
            <tr>
              <th style={thStyle}>Sl. No</th>
              <th style={thStyle}>Plan Name</th>
              <th style={thStyle}>Service Category</th>
              <th style={thSortable} onClick={() => handleSort("type")}>
                Type <SortIcon col="type" sortBy={sortBy} sortOrder={sortOrder} />
              </th>
              <th style={thSortable} onClick={() => handleSort("pricing")}>
                Pricing <SortIcon col="pricing" sortBy={sortBy} sortOrder={sortOrder} />
              </th>
              <th style={thSortable} onClick={() => handleSort("subscriberCount")}>
                Active Subscribers <SortIcon col="subscriberCount" sortBy={sortBy} sortOrder={sortOrder} />
              </th>
              <th style={thSortable} onClick={() => handleSort("status")}>
                Status <SortIcon col="status" sortBy={sortBy} sortOrder={sortOrder} />
              </th>
              <th style={thStyle}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? skeletonRows.map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 8 }).map((__, j) => (
                      <td key={j} style={tdStyle}>
                        <Skeleton height="16px" marginBottom="0" />
                      </td>
                    ))}
                  </tr>
                ))
              : data.length === 0
              ? (
                  <tr>
                    <td colSpan={8} style={{ ...tdStyle, textAlign: "center", color: "#6B7280", padding: "48px" }}>
                      No subscription plans found
                    </td>
                  </tr>
                )
              : data.map((row, i) => (
                  <tr
                    key={row.id}
                    onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    style={{ transition: "background 0.15s" }}
                  >
                    <td style={tdStyle}>{(page - 1) * limit + i + 1}</td>
                    <td style={{ ...tdStyle, fontWeight: "600", color: "#111827" }}>{row.planName}</td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          border: "1px solid #D1D5DB",
                          background: "#fff",
                          color: "#374151",
                          borderRadius: "6px",
                          padding: "3px 10px",
                          fontSize: "13px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.serviceType}
                      </span>
                    </td>
                    <td style={tdStyle}><TypeBadge type={row.type} /></td>
                    <td style={{ ...tdStyle, fontWeight: "600", color: "#111827" }}>
                      {currency} {Number(row.pricing || 0).toFixed(2)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "36px",
                          height: "28px",
                          background: "#F3F4F6",
                          borderRadius: "8px",
                          fontWeight: "700",
                          fontSize: "13px",
                          color: "#374151",
                        }}
                      >
                        {row.subscriberCount ?? 0}
                      </span>
                    </td>
                    <td style={tdStyle}><StatusBadge status={row.status} /></td>
                    <td style={tdStyle}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "nowrap" }}>
                        {(() => {
                          const isActive = row.status === "Active";
                          const editReason = getEditDisabledReason(row);
                          const inactivateReason = getInactivateDisabledReason(row);
                          return (
                            <>
                              <button
                                onClick={isActive ? () => onPurchase?.(row) : undefined}
                                disabled={!isActive}
                                title={isActive ? "Purchase plan" : "Plan is inactive"}
                                style={{
                                  background: isActive ? "#C0335A" : "#F3F4F6",
                                  border: "none",
                                  borderRadius: "8px",
                                  padding: "6px 12px",
                                  cursor: isActive ? "pointer" : "not-allowed",
                                  color: isActive ? "#fff" : "#9CA3AF",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "5px",
                                  fontSize: "13px",
                                  fontWeight: "600",
                                  opacity: isActive ? 1 : 0.6,
                                  transition: "background 0.2s",
                                }}
                                onMouseEnter={e => { if (isActive) e.currentTarget.style.background = "#a52b4e"; }}
                                onMouseLeave={e => { if (isActive) e.currentTarget.style.background = "#C0335A"; }}
                              >
                                <FiShoppingCart size={14} /> Purchase
                              </button>
                              <button
                                onClick={editReason ? undefined : () => onEdit?.(row)}
                                disabled={!!editReason}
                                title={editReason || "Edit plan"}
                                style={{
                                  background: editReason ? "#F9FAFB" : "#EFF6FF",
                                  border: `1px solid ${editReason ? "#E5E7EB" : "#BFDBFE"}`,
                                  borderRadius: "8px",
                                  padding: "6px 10px",
                                  cursor: editReason ? "not-allowed" : "pointer",
                                  color: editReason ? "#9CA3AF" : "#2563EB",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "5px",
                                  fontSize: "13px",
                                  fontWeight: "600",
                                  opacity: editReason ? 0.6 : 1,
                                  transition: "all 0.2s",
                                }}
                                onMouseEnter={e => { if (!editReason) { e.currentTarget.style.background = "#DBEAFE"; e.currentTarget.style.borderColor = "#93C5FD"; } }}
                                onMouseLeave={e => { if (!editReason) { e.currentTarget.style.background = "#EFF6FF"; e.currentTarget.style.borderColor = "#BFDBFE"; } }}
                              >
                                <FiEdit2 size={14} /> Edit
                              </button>
                              {isActive ? (
                                <button
                                  onClick={inactivateReason ? undefined : () => onInactivate?.(row)}
                                  disabled={!!inactivateReason}
                                  title={inactivateReason || "Inactivate plan"}
                                  style={{
                                    background: inactivateReason ? "#F9FAFB" : "#FFF7ED",
                                    border: `1px solid ${inactivateReason ? "#E5E7EB" : "#FED7AA"}`,
                                    borderRadius: "8px",
                                    padding: "6px 10px",
                                    cursor: inactivateReason ? "not-allowed" : "pointer",
                                    color: inactivateReason ? "#9CA3AF" : "#C2410C",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "5px",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    opacity: inactivateReason ? 0.6 : 1,
                                    transition: "all 0.2s",
                                  }}
                                  onMouseEnter={e => { if (!inactivateReason) { e.currentTarget.style.background = "#FFEDD5"; e.currentTarget.style.borderColor = "#FDBA74"; } }}
                                  onMouseLeave={e => { if (!inactivateReason) { e.currentTarget.style.background = "#FFF7ED"; e.currentTarget.style.borderColor = "#FED7AA"; } }}
                                >
                                  <FiPower size={14} /> Inactivate
                                </button>
                              ) : (
                                <button
                                  onClick={() => onReactivate?.(row)}
                                  title="Reactivate plan"
                                  style={{
                                    background: "#F0FDF4",
                                    border: "1px solid #86EFAC",
                                    borderRadius: "8px",
                                    padding: "6px 10px",
                                    cursor: "pointer",
                                    color: "#15803D",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "5px",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    transition: "all 0.2s",
                                  }}
                                  onMouseEnter={e => { e.currentTarget.style.background = "#DCFCE7"; e.currentTarget.style.borderColor = "#4ADE80"; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = "#F0FDF4"; e.currentTarget.style.borderColor = "#86EFAC"; }}
                                >
                                  <FiPower size={14} /> Reactivate
                                </button>
                              )}
                              <button
                                onClick={() => navigate(`/subscription/${row.id}`)}
                                title="View plan details"
                                style={{
                                  background: "none",
                                  border: "1px solid #E5E7EB",
                                  borderRadius: "8px",
                                  padding: "6px 10px",
                                  cursor: "pointer",
                                  color: "#6B7280",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  transition: "all 0.2s",
                                }}
                                onMouseEnter={e => { e.currentTarget.style.color = "#C0335A"; e.currentTarget.style.borderColor = "#C0335A"; e.currentTarget.style.background = "#FDE8EF"; }}
                                onMouseLeave={e => { e.currentTarget.style.color = "#6B7280"; e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.background = "none"; }}
                              >
                                <FiEye size={16} />
                              </button>
                            </>
                          );
                        })()}
                      </div>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card view */}
      <div className="subscription-table-mobile" style={{ display: "none", padding: "12px" }}>
        <MobileCardView />
      </div>

      {/* Pagination */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 16px 0",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div style={{ fontSize: "13px", color: "#6B7280" }}>
          Showing {total === 0 ? 0 : (page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <select
            value={limit}
            onChange={e => { onLimitChange(Number(e.target.value)); onPageChange(1); }}
            style={{
              border: "1px solid #D1D5DB",
              borderRadius: "8px",
              padding: "6px 10px",
              fontSize: "13px",
              color: "#374151",
              cursor: "pointer",
            }}
          >
            {[10, 20, 50].map(l => <option key={l} value={l}>{l} per page</option>)}
          </select>
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            style={{
              border: "1px solid #D1D5DB",
              borderRadius: "8px",
              padding: "6px 14px",
              fontSize: "13px",
              background: "#fff",
              color: page <= 1 ? "#D1D5DB" : "#374151",
              cursor: page <= 1 ? "not-allowed" : "pointer",
            }}
          >
            Previous
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let p;
            if (totalPages <= 5) p = i + 1;
            else if (page <= 3) p = i + 1;
            else if (page >= totalPages - 2) p = totalPages - 4 + i;
            else p = page - 2 + i;
            return (
              <button
                key={p}
                onClick={() => onPageChange(p)}
                style={{
                  border: `1px solid ${p === page ? "#C0335A" : "#D1D5DB"}`,
                  borderRadius: "8px",
                  padding: "6px 12px",
                  fontSize: "13px",
                  background: p === page ? "#C0335A" : "#fff",
                  color: p === page ? "#fff" : "#374151",
                  cursor: "pointer",
                  fontWeight: p === page ? "600" : "400",
                  minWidth: "36px",
                }}
              >
                {p}
              </button>
            );
          })}
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            style={{
              border: "1px solid #D1D5DB",
              borderRadius: "8px",
              padding: "6px 14px",
              fontSize: "13px",
              background: "#fff",
              color: page >= totalPages ? "#D1D5DB" : "#374151",
              cursor: page >= totalPages ? "not-allowed" : "pointer",
            }}
          >
            Next
          </button>
        </div>
      </div>

      <style>{`
        @media (max-width: 767px) {
          .subscription-table-desktop { display: none !important; }
          .subscription-table-mobile { display: block !important; }
        }
      `}</style>
    </div>
  );
};

export default SubscriptionTable;
